import { IMAGE_URL } from "constants/ApiConstant";

/**
 * Construction des URLs de fichiers servis par l'API (uploads/images/...).
 *
 * Les noms de fichiers uploadés gardent le nom d'origine : ils contiennent
 * souvent des espaces, des virgules ou des accents
 * ("1786259826129ChatGPT Image 4 aoÃ»t 2026, 16_24_04.png").
 * Ces caractères doivent être encodés, sinon :
 *  - en CSS, `background-image: url(...)` non échappé devient invalide
 *    (espace + virgule cassent le parsing) et l'image ne s'affiche pas ;
 *  - en `href`, le lien peut être tronqué.
 */

// Caractères autorisés tels quels dans un segment de chemin (RFC 3986) que
// encodeURIComponent encode inutilement : on les restaure pour obtenir la même
// URL que celle produite par le navigateur.
const PATH_SAFE = {
  "%2C": ",",
  "%3A": ":",
  "%40": "@",
  "%21": "!",
  "%24": "$",
  "%26": "&",
  "%27": "'",
  "%28": "(",
  "%29": ")",
  "%2A": "*",
  "%3B": ";",
  "%3D": "=",
};

// Encode un segment de chemin sans ré-encoder ce qui l'est déjà (%20 -> %20, pas %2520)
const encodeSegment = (segment = "") =>
  segment
    .split(/(%[0-9A-Fa-f]{2})/)
    .map((part) =>
      /^%[0-9A-Fa-f]{2}$/.test(part)
        ? part
        : encodeURIComponent(part).replace(
            /%(2C|3A|40|21|24|26|27|28|29|2A|3B|3D)/g,
            (match) => PATH_SAFE[match.toUpperCase()] || match
          )
    )
    .join("");

const encodePath = (path = "") => path.split("/").map(encodeSegment).join("/");

/**
 * Extrait le chemin brut, quelle que soit la forme renvoyée par l'API :
 * "uploads/images/x.png" | { path } | { filename } | { url }
 */
const resolveRawPath = (image) => {
  if (!image) return "";
  if (typeof image === "string") return image;
  return image.path || image.url || image.filename || "";
};

/**
 * URL absolue et encodée d'un fichier uploadé. Renvoie "" si aucune image.
 */
export const buildFileUrl = (image, baseUrl = IMAGE_URL) => {
  const raw = resolveRawPath(image);
  if (!raw) return "";

  // Les chemins Windows renvoyés par multer utilisent des backslashes
  const normalized = String(raw).trim().replace(/\\/g, "/");
  if (!normalized) return "";

  if (/^(data:|blob:)/i.test(normalized)) return normalized;

  if (/^https?:\/\//i.test(normalized)) {
    const pathStart = normalized.indexOf("/", normalized.indexOf("://") + 3);
    if (pathStart === -1) return normalized;
    return normalized.slice(0, pathStart) + encodePath(normalized.slice(pathStart));
  }

  const base = (baseUrl || "").replace(/\/+$/, "");
  return `${base}/${encodePath(normalized.replace(/^\/+/, ""))}`;
};

/** Alias explicite pour les images. */
export const buildImageUrl = buildFileUrl;

/**
 * Valeur prête pour `background-image` (guillemets obligatoires quand l'URL
 * contient des espaces ou des virgules). Renvoie "none" si pas d'image.
 */
export const toCssUrl = (url) =>
  url ? `url("${String(url).replace(/["\\]/g, "\\$&")}")` : "none";
